/**
 * Channel Module — shared channel selector logic for all tools.
 *
 * Handles: format→channel mapping, auto-detection from file, UI rendering,
 * FFmpeg args generation. Drop-in for any tool with a format selector.
 *
 * Usage:
 *   ChannelModule.init('channelSelect', { formatEl: 'formatSelect' });
 *   ChannelModule.detect(audioBuffer);           // auto-detect from file
 *   ChannelModule.getArgs();                     // ['-ac','2'] or []
 *   ChannelModule.updateForFormat('mp3');         // re-render options
 */
(function(){
'use strict';

// Per-format channel ceilings. SINGLE SOURCE OF TRUTH is
// config/formats.php 'max_channels' (measured 2026-07-21 on the shipped WASM
// build), delivered via window.__FFMPEG_CONFIG__.maxChannels. The lists below
// are only the fallback for pages that render without the config — they were
// the primary source before, and their old comment ("AAC WASM can't do 7.1")
// had gone stale: the current build encodes real 5.1 AND 7.1 AAC.
var FALLBACK_MAX_STEREO = ['mp3','amr','gsm','g722','m4r','wma','opus','webm','mp2','spx','mmf','ra'];
var FALLBACK_MAX_51 = ['ac3','eac3','dts','ogg','oga','mka'];

function formatMax(fmt){
    fmt = (fmt || '').toLowerCase();
    if(!fmt) return 8;
    var cfg = window.__FFMPEG_CONFIG__ && window.__FFMPEG_CONFIG__.maxChannels;
    if(cfg && typeof cfg[fmt] === 'number' && cfg[fmt] > 0) return cfg[fmt];
    if(cfg) return 2;   // config present but format unknown ⇒ conservative
    // No config on this page — legacy fallback lists.
    if(FALLBACK_MAX_STEREO.indexOf(fmt) !== -1) return 2;
    if(FALLBACK_MAX_51.indexOf(fmt) !== -1) return 6;
    return 8;
}

// Localized labels — full 9-locale coverage (en duplicates the call-site
// fallback on purpose so the completeness guard can treat every map alike).
var LABELS = {
    auto:   { en: 'Auto',   ru: 'Авто',    ko: '자동',     ar: 'تلقائي',   pt: 'Auto',     id: 'Otomatis', de: 'Auto',    es: 'Auto',    fr: 'Auto', uk: 'Авто', tr: 'Otomatik' },
    mono:   { en: 'Mono',   ru: 'Моно',    ko: '모노',     ar: 'أحادي',    pt: 'Mono',     id: 'Mono',     de: 'Mono',    es: 'Mono',    fr: 'Mono', uk: 'Моно', tr: 'Mono' },
    stereo: { en: 'Stereo', ru: 'Стерео',  ko: '스테레오', ar: 'ستيريو',   pt: 'Estéreo',  id: 'Stereo',   de: 'Stereo',  es: 'Estéreo', fr: 'Stéréo', uk: 'Стерео', tr: 'Stereo' },
    // Suffix on channel options the SOURCE cannot fill. Offering 5.1 for a
    // stereo file used to emit -ac 6 = a file labeled 5.1 with FOUR digitally
    // silent channels (measured: FC/LFE/BL/BR at -inf dB). Fake surround is
    // the channel twin of upscaling, so these options are now disabled with
    // this hint — mirror of ResolutionModule's "higher than source".
    // "more than source" was read as "bigger file than the source" — a user
    // asked what it meant, since of course an export is larger (bug-report
    // 2026-07-29 on /karaoke). It never meant size: the SOURCE has fewer
    // channels than the option needs. Say that instead, and name what the
    // source actually is — "source: Stereo" leaves nothing to interpret.
    not_in_source: { en: 'source: {ch}', ru: 'исходник: {ch}', ko: '원본: {ch}', ar: 'المصدر: {ch}', pt: 'origem: {ch}', id: 'sumber: {ch}', de: 'Quelle: {ch}', es: 'origen: {ch}', fr: 'source : {ch}', uk: 'джерело: {ch}', tr: 'kaynak: {ch}' },
    // Shown INSTEAD of not_in_source when the tool can run a real matrix upmix
    // (UpmixModule). The wording must stay honest — the channels are derived
    // from the stereo content, the recording was never 5.1.
    upmixed: { en: 'upmixed from stereo', ru: 'апмикс из стерео', ko: '스테레오 업믹스', ar: 'توسيع من ستيريو', pt: 'upmix de estéreo', id: 'upmix dari stereo', de: 'Upmix aus Stereo', es: 'upmix desde estéreo', fr: 'upmix depuis la stéréo', uk: 'апмікс зі стерео', tr: 'stereodan genişletilmiş' },
    // Tooltip on the surround chip — says WHOSE channel count it reports.
    source_channels: { en: 'Source file channels', ru: 'Каналы исходного файла', ko: '원본 파일 채널', ar: 'قنوات الملف المصدر', pt: 'Canais do arquivo original', id: 'Kanal file sumber', de: 'Kanäle der Quelldatei', es: 'Canales del archivo original', fr: 'Canaux du fichier source', uk: 'Канали вихідного файлу', tr: 'Kaynak dosya kanalları' }
};

var _selectEl = null;
var _formatEl = null;
var _detected = null;  // auto-detected channel count from file
var _autoDownmixWarned = false; // auto-path fold disclosed once per page
var _lastFormat = '';  // last format set via updateForFormat() — fallback when no <select> picker exists
var _upmixEnabled = false; // tool opted into matrix upmix via init({upmix:true})
var _toolName = '';
var _locale = (window.__LOCALE || document.documentElement.lang || 'en').substring(0, 2);

// Downmix warning labels
var DOWNMIX_WARN = {
    en: 'will be downmixed to',
    ru: 'будет сведён к',
    ko: '으로 다운믹스됩니다',
    ar: 'سيتم تحويله إلى',
    id: 'akan di-downmix ke',
    pt: 'será convertido para',
    de: 'wird heruntergemischt auf',
    es: 'se convertirá a',
    fr: 'sera converti en',
    uk: 'буде зведено до',
    tr: 'indirgenecek:'
};
var CH_NAMES = {
    1: { en:'Mono', ru:'Моно', ko:'모노', ar:'أحادي', id:'Mono', pt:'Mono', de:'Mono', es:'Mono', fr:'Mono', uk:'Моно', tr:'Mono' },
    2: { en:'Stereo', ru:'Стерео', ko:'스테레오', ar:'ستيريو', id:'Stereo', pt:'Estéreo', de:'Stereo', es:'Estéreo', fr:'Stéréo', uk:'Стерео', tr:'Stereo' },
    6: { en:'5.1 Surround', ru:'5.1', ko:'5.1', ar:'5.1', id:'5.1', pt:'5.1', de:'5.1', es:'5.1', fr:'5.1', uk:'5.1', tr:'5.1' },
    8: { en:'7.1 Surround', ru:'7.1', ko:'7.1', ar:'7.1', id:'7.1', pt:'7.1', de:'7.1', es:'7.1', fr:'7.1', uk:'7.1', tr:'7.1' }
};

function label(key, fallback){
    if(key === '6') return '5.1 Surround';
    if(key === '8') return '7.1 Surround';
    var map = LABELS[key];
    return map && map[_locale] ? map[_locale] : fallback;
}

// Ceiling used for RENDERING the option list. Differs from formatMax() in
// exactly one case: format unknown (a tool whose picker is a custom control
// and which hasn't called updateForFormat yet). formatMax returns 8 there —
// which made the list advertise 5.1/7.1 on tools that export MP3, the bug
// behind the 2026-07-23 «Почему 5.1 не работает?» report. Offering nothing
// above stereo until the format is known is the honest default.
//
// Deliberately NOT folded into formatMax(): getArgs() uses formatMax for the
// export clamp, and clamping an unknown format to 2 would make 'auto' emit
// `-ac 2` and SILENTLY DOWNMIX a 5.1 source on the "same format as input"
// tools (trim / audio-looper / add-music-to-video). Conservative display,
// unchanged export — the two must not share one number.
function renderMax(format){
    if(!format) return 2;
    return formatMax(format);
}

// True when this tool may synthesise `target` channels from the detected
// source. Requires all three: the tool opted in, the module is really loaded
// (not the boot stub), and the module itself accepts the pair (stereo only —
// a mono source has no side signal, so its "surround" would be silence).
// Consumer notification (see init's opts.onChange). Fires on a user change of
// the picker AND after detect() lands, because detection arrives from a
// background probe and can land AFTER the pick — until it does,
// getUpmixTarget() answers null (isUpmix needs a known stereo source) and a
// consumer pricing the export would still be quoting the fast lane. One seam,
// both directions; never throws into the caller's flow.
// `cause` tells the consumer WHY it fired: 'user' — the person changed the
// picker; 'detect' — the background probe finally learned the source layout,
// which can change getUpmixTarget() with no interaction at all. A consumer
// that only recomputes a number wants both; one that also LOGS an intent must
// separate them, or a detection lands in the analytics as a user choice.
// (Caught on a real 361 MB run: two `audio_channels_pick` events before the
// operator had touched anything.)
var _onChange = null;
function notifyChange(cause){
    if(!_onChange) return;
    try { _onChange({ cause: cause || 'user', value: window.ChannelModule.getValue(),
                      detected: _detected, upmix: window.ChannelModule.getUpmixTarget() }); } catch(e){}
}

// sourceCh defaults to the detected file; plan() asks about any file.
function canUpmixTo(target, sourceCh){
    return _upmixEnabled
        && !!window.UpmixModule
        && !window.UpmixModule.__isStub
        && typeof window.UpmixModule.isUpmix === 'function'
        && window.UpmixModule.isUpmix(target, sourceCh === undefined ? _detected : sourceCh);
}

function getOptions(format){
    var maxCh = renderMax(format);
    var opts = [
        { value: 'auto', label: label('auto', 'Auto') },
        { value: '1',    label: label('mono', 'Mono') }
    ];
    if(maxCh >= 2) opts.push({ value: '2', label: label('stereo', 'Stereo') });
    if(maxCh >= 6) opts.push({ value: '6', label: '5.1 Surround' });
    if(maxCh >= 8) opts.push({ value: '8', label: '7.1 Surround' });
    // Source clamp: a surround option the source cannot fill stays VISIBLE but
    // disabled, with a hint — never offered as if it were real output. Only
    // applies when we actually detected the source (a file); tools without a
    // detect (generators, pre-upload state) keep the format ladder untouched.
    //
    // EXCEPTION: a tool that opted into upmix (init({upmix:true})) and has
    // UpmixModule loaded can genuinely DERIVE those channels from a stereo
    // source, so the option is offered — labelled as an upmix, never as if the
    // recording had been surround. The opt-in is per tool on purpose: without
    // it a tool would advertise an option its export path cannot honour, and
    // the user would get a silent stereo file labelled 5.1.
    if(_detected && _detected > 0){
        for(var i = 0; i < opts.length; i++){
            var n = parseInt(opts[i].value, 10);
            if(!isNaN(n) && n > 2 && n > _detected){
                if(canUpmixTo(n)){
                    opts[i].label += ' · ' + label('upmixed', 'upmixed from stereo');
                } else {
                    opts[i].disabled = true;
                    opts[i].label += ' · ' + label('not_in_source', 'source: {ch}')
                        .replace('{ch}', chName(_detected));
                }
            }
        }
    }
    return opts;
}

function chName(n){
    var map = CH_NAMES[n];
    return map ? (map[_locale] || map.en) : n + 'ch';
}

/* Surround badge: tells the user the SOURCE file is multichannel. The select
   next to it shows the OUTPUT — the two carry different facts, which is why
   both can read "5.1" at once (source 5.1 + Auto) or differ (source 5.1 +
   Stereo picked). */
function updateBadge(){
    if(!_selectEl) return;
    var badge = _selectEl.parentElement && _selectEl.parentElement.querySelector('.ch-badge');
    if(!badge){
        badge = document.createElement('span');
        badge.className = 'ch-badge';
        // flex:0 0 auto + align-self:center keep it a compact pill wherever it
        // lands: hosts range from a horizontal control row to a full-width
        // flex COLUMN (audio-looper), and as a stretched flex item the chip
        // spanned ~890px instead of hugging its text.
        badge.style.cssText = 'display:none;align-items:center;font-size:12px;font-weight:600;' +
            'padding:3px 9px;border-radius:999px;margin-inline-start:8px;white-space:nowrap;' +
            'line-height:1.25;vertical-align:middle;flex:0 0 auto;align-self:center;width:fit-content';
        // Anchor it to the control it describes, NOT to the end of the row.
        // appendChild put the badge after whatever came last in the container
        // — on /trim that is the Save button, so the "5.1" chip rendered past
        // the primary action, reading as an orphan. afterend keeps the pair
        // (select + badge) together in every layout that hosts them.
        _selectEl.insertAdjacentElement('afterend', badge);
    }
    if(_detected && _detected > 2){
        badge.textContent = chName(_detected);
        // Full label on hover — the bare "5.1" chip next to an "Auto (5.1)"
        // select otherwise reads as a duplicate rather than as source info.
        badge.title = label('source_channels', 'Source file channels') + ': ' + chName(_detected);
        badge.style.display = 'inline-flex';
        badge.style.background = 'rgba(139,92,246,0.14)';
        badge.style.color = '#a78bfa';
        badge.style.border = '1px solid rgba(139,92,246,0.28)';
    } else {
        badge.style.display = 'none';
    }
}

/* Warn user when downmixing surround to fewer channels */
function checkDownmix(){
    if(!_selectEl || !_detected || _detected <= 2) return;
    var selected = parseInt(_selectEl.value, 10);
    if(!selected || isNaN(selected)) return; // auto — no warning
    if(selected < _detected){
        var warnText = chName(_detected) + ' ' + (DOWNMIX_WARN[_locale] || DOWNMIX_WARN.en) + ' ' + chName(selected);
        if(window.__toast) window.__toast(warnText, 'warning');
    }
}

function render(format){
    if(!_selectEl) return;
    var cur = _selectEl.value;
    var opts = getOptions(format);
    _selectEl.innerHTML = '';
    var validValues = [];
    opts.forEach(function(o){
        var opt = document.createElement('option');
        opt.value = o.value;
        opt.textContent = o.label;
        if(o.disabled) opt.disabled = true;
        _selectEl.appendChild(opt);
        if(!o.disabled) validValues.push(o.value);
    });
    // Restore previous selection if still valid AND not disabled (a stale 5.1
    // pick must not survive a stereo file landing); otherwise auto.
    if(validValues.indexOf(cur) !== -1){
        _selectEl.value = cur;
    } else {
        _selectEl.value = 'auto';
    }
    // Auto hint shows what auto will actually DELIVER — min(source, format
    // ceiling) — not the raw source count. "Auto (6ch)" on an MP3 export was
    // a lie: the encoder folds it to stereo. Honest: "Auto (Stereo)".
    if(_detected && _selectEl.value === 'auto'){
        var autoOpt = _selectEl.querySelector('option[value="auto"]');
        if(autoOpt){
            var outCh = Math.min(_detected, formatMax(format));
            autoOpt.textContent = label('auto','Auto') + ' (' + chName(outCh) + ')';
        }
    }
}

// Common format-select IDs across the codebase. If init() wasn't given one,
// the module auto-discovers it from the DOM so clamp logic in getArgs still
// has the format context it needs.
// Auto-discovery list — IDs of <select> elements that hold the output
// format. Tools should pass `formatEl: 'theirId'` to init() explicitly,
// but auto-discovery is the safety net for tools that forgot
// (caught the /pitch tool's `ptExportFormat` not being in this list,
// causing the channel matrix to render all 7.1 options for MP3 — user
// 2026-05-07).
// 2026-07-23 sweep (user report «Почему 5.1 не работает?» on /equalizer):
// audited every ChannelModule consumer; these are all the <select>-based
// format pickers whose ids were NOT discoverable, which made the channel
// options render the full 5.1/7.1 matrix at load regardless of format
// (their change handlers were already wired via updateForFormat).
var FORMAT_EL_IDS = ['format-select','formatSelect','ac-format','outputFormat','export-format','fmt-select','ptExportFormat',
    'eqFormat','ai-fmt','asFmt','ebFormat','lsExpFmt','mtFmtSelect','peFormat','spExportFormat'];
function resolveFormatEl(){
    if(_formatEl) return _formatEl;
    for(var i=0;i<FORMAT_EL_IDS.length;i++){
        var el=document.getElementById(FORMAT_EL_IDS[i]);
        if(el && el.tagName==='SELECT'){ _formatEl=el; return el; }
    }
    return null;
}
function getCurrentFormat(){
    var el=resolveFormatEl();
    if(el && el.value) return el.value;
    // Fallback for tools whose format picker is NOT a discoverable <select>
    // (e.g. denoise's custom dropdown). updateForFormat() records the last
    // format the tool told us; without this, detect()'s re-render called
    // getCurrentFormat()='' → the all-options (5.1/7.1) branch, overriding
    // the correct mono/stereo restriction a prior updateForFormat('mp3') set
    // (feedback 2026-06-14: MP3 offered 5.1/7.1 Surround). Same class as the
    // /pitch ptExportFormat bug noted above, fixed at the module level.
    return _lastFormat || '';
}

window.ChannelModule = {
    /**
     * Initialize the channel selector.
     * @param {string} selectId — id of the <select> element
     * @param {object} opts — { formatEl, tool, upmix, onChange }
     *
     * opts.onChange — called after every user change of the picker.
     *   Added 2026-08-09. This module owns a control that can change what an
     *   export COSTS — picking 5.1 on a stereo source turns on a matrix upmix,
     *   which the WebGL rig cannot do, so the whole video drops to the software
     *   encoder — and it had no way to tell anyone. /enhance-video showed the
     *   price of that on the button, and the button was never recomputed:
     *   measured on a real 230 MB file, the CTA kept reading «~44 сек» while the
     *   run had become ~11 min, and on refund №13's 861 MB 4K it promised ~5 min
     *   for a job that takes ~87. The tool ALSO renders a "surround costs you N"
     *   hint from the same recompute, so that hint was dead on the one
     *   interaction it exists for.
     *   Every consumer that prices its export needs this seam; bolting a private
     *   listener onto a foreign element in each of them is how the next tool
     *   inherits the same silence. Optional — tools that don't pass it are
     *   untouched.
     */
    init: function(selectId, opts){
        opts = opts || {};
        _selectEl = document.getElementById(selectId);
        if(!_selectEl) return;
        // Opt-in: only a tool whose export path actually calls UpmixModule may
        // offer surround for a stereo source. See canUpmixTo().
        _upmixEnabled = !!opts.upmix;

        if(opts.formatEl){
            _formatEl = document.getElementById(opts.formatEl);
            if(_formatEl){
                _formatEl.addEventListener('change', function(){
                    render(_formatEl.value);
                });
            }
        }

        // Initial render — always starts at "auto" (= inherit source
        // channels) per fresh session. Previously we persisted the last
        // selection via __toolSettings, which caused the acx-mastering
        // stereo-export regression: user picked "Stereo" once in audio-
        // enhancer, the LocalStorage entry survived, and three weeks
        // later acx-mastering silently downmixed a mono recording to
        // stereo on export — violating ACX. Per-session state only:
        // the user's explicit override holds during the current visit,
        // but a page reload / new visit starts from "auto" (= honor the
        // source file's channel layout) every time.
        render(getCurrentFormat());
        _toolName = opts.tool || location.pathname.split('/').pop();
        _onChange = (typeof opts.onChange === 'function') ? opts.onChange : null;
        _selectEl.addEventListener('change', function(){
            checkDownmix();
            notifyChange('user');
        });
    },

    /**
     * Auto-detect channels from AudioBuffer or number.
     * Updates the "Auto" option label to show detected value.
     * @param {AudioBuffer|number} bufferOrNum
     */
    detect: function(bufferOrNum){
        if(typeof bufferOrNum === 'number'){
            _detected = bufferOrNum;
        } else if(bufferOrNum && bufferOrNum.numberOfChannels){
            _detected = bufferOrNum.numberOfChannels;
        }
        // Re-render to update Auto label + show surround badge
        render(getCurrentFormat());
        updateBadge();
        // Detection can land AFTER the user already picked 5.1 (it comes from a
        // background probe). getUpmixTarget() only answers once the source is
        // known to be stereo, so this is the moment a consumer's price becomes
        // computable — tell it. Same lie as a missed change event, reached from
        // the other side.
        notifyChange('detect');
    },

    /**
     * Update options for a specific format (called externally if format changes).
     * @param {string} format
     */
    updateForFormat: function(format){
        _lastFormat = format || '';
        render(_lastFormat);
    },

    /**
     * Get FFmpeg args for the selected channels.
     * @returns {Array} e.g. ['-ac','2'] or [] for auto
     */
    getArgs: function(){
        if(!_selectEl) return [];
        var val = _selectEl.value;
        // Telemetry: log channel selection on export
        var fmtCur = (getCurrentFormat() || '').toLowerCase();
        // Telemetry. Payload rides in `src`: logClientError persists only
        // type|src|page — the old shape put channels/format/detected in their
        // own JSON fields and the server DROPPED them, so 13k channel_export
        // rows carried an empty src and the instrument was blind (found
        // 2026-07-21 when this data was first actually needed).
        try {
            navigator.sendBeacon('/api/log-error', JSON.stringify([{
                type: 'channel_export',
                src: _toolName + '|sel=' + val + '|fmt=' + (fmtCur || 'unknown') + '|det=' + (_detected || 0),
                page: location.pathname
            }]));
        } catch(e){}
        // Clamp to what the target format's encoder actually supports.
        // Without this, a stale selection (user picked 7.1 when format was FLAC,
        // then switched to MP3 without the <select> options being refreshed in
        // time — observed on Yandex Browser Android) leaks "7.1" into the args
        // and libmp3lame rejects with "channel layout '7.1' is not supported".
        var maxCh = formatMax(fmtCur);
        if(!val || val === 'auto'){
            // 'auto' = inherit source channels, downmixed to encoder max.
            if(_detected && _detected > maxCh){
                // Disclose the fold ONCE — the explicit-selection path warns
                // via checkDownmix(), but auto used to fold 5.1 into an MP3's
                // stereo silently. Same DOWNMIX_WARN wording, once per page.
                if(!_autoDownmixWarned){
                    _autoDownmixWarned = true;
                    try{
                        if(window.__toast) window.__toast(
                            chName(_detected) + ' ' + (DOWNMIX_WARN[_locale] || DOWNMIX_WARN.en) + ' ' + chName(maxCh),
                            'warning');
                    }catch(_w){}
                }
                return ['-ac', String(maxCh)];
            }
            return [];
        }
        var n = parseInt(val, 10);
        if(isNaN(n) || n < 1) return [];
        if(n > maxCh) n = maxCh;
        // Upmix selection: emit NOTHING here. `-ac 6` is precisely the fake
        // this feature replaces (it yields four digitally silent channels), and
        // the real matrix lives in a filter graph the tool must add via
        // UpmixModule.ffmpegArgs(). Returning [] keeps a tool that forgot that
        // step honest — it exports stereo rather than a mislabelled 5.1.
        if(canUpmixTo(n)) return [];
        // Source clamp, mirror of the option-level disable: never ASK the
        // encoder for more channels than the source has (a stale/persisted
        // selection could bypass the disabled <option>). Mono→stereo (2) stays
        // allowed — that is a standard conversion; fabricating >2 is not.
        if(_detected && _detected > 0 && n > 2 && n > _detected) n = Math.max(2, _detected);
        return ['-ac', String(n)];
    },

    /**
     * Get the numeric channel value (for use in filter_complex etc).
     * Returns null for 'auto'.
     * @returns {number|null}
     */
    getValue: function(){
        if(!_selectEl) return null;
        var val = _selectEl.value;
        if(!val || val === 'auto') return null;
        return parseInt(val, 10);
    },

    /**
     * Get the channel layout string for FFmpeg filter_complex.
     * @returns {string} e.g. 'stereo', 'mono', '5.1', '7.1', or '' for auto
     */
    getLayout: function(){
        var v = this.getValue();
        if(!v) return '';
        switch(v){
            case 1: return 'mono';
            case 2: return 'stereo';
            case 6: return '5.1';
            case 8: return '7.1';
            default: return '';
        }
    },

    /**
     * Get detected channel count (from file).
     * @returns {number|null}
     */
    getDetected: function(){
        return _detected;
    },

    /**
     * Did the user pick a channel count that has to be SYNTHESISED from the
     * source (stereo → 5.1/7.1)? Tools branch on this to add the upmix graph
     * (FFmpeg pipelines) or call UpmixModule.processBuffer (Web Audio ones).
     * @returns {number|null} target channel count, or null when no upmix applies
     */
    getUpmixTarget: function(){
        var v = this.getValue();
        return (v && canUpmixTo(v)) ? v : null;
    },

    /**
     * Check if a format supports surround (5.1/7.1).
     * @param {string} format
     * @returns {boolean}
     */
    supportsSurround: function(format){
        // Was `MAX_STEREO.indexOf(...)` — a ReferenceError since the list was
        // renamed FALLBACK_MAX_STEREO (2026-07-21). No live caller tripped it
        // (dead API until now), but formatMax is the real authority anyway.
        return formatMax(format) > 2;
    },

    /**
     * Resolve the channel count an export should actually be rendered at:
     * explicit selection or source count, clamped by the format's encoder
     * ceiling and by the source (never fabricate >2ch from fewer — the
     * fake-surround ban, same rule as getArgs). Tools that render audio
     * themselves (OfflineAudioContext) use this so their render graph, their
     * WAV writer and the FFmpeg -ac args all agree on one number.
     * @param {string} format — target output format
     * @param {number} sourceCh — channel count of the loaded file
     * @returns {number}
     */
    outputChannels: function(format, sourceCh){
        var maxCh = formatMax((format || '').toLowerCase());
        var sel = this.getValue();           // null = auto → inherit source
        var n = sel || sourceCh || 2;
        if(n > maxCh) n = maxCh;
        if(sourceCh > 0 && n > 2 && n > sourceCh) n = Math.max(2, sourceCh);
        return Math.max(1, n);
    },

    /**
     * The channel plan for ONE file: the count to render at, and the upmix
     * target when the surround layout must be derived from stereo (null when
     * not). getUpmixTarget() answers only for the file detect() saw; a batch
     * holds files with their own layouts — a mono or a 5.1 track in a folder
     * of stereo ones must not inherit the first file's answer. Same rules as
     * outputChannels() and the upmix gate, asked about this source.
     * @param {string} format — target output format
     * @param {number} sourceCh — channels of that file
     * @param {string} [selection] — 'auto' asks for the source's own layout
     *   regardless of the picker (a batch exporting «as in the source»)
     * @returns {{channels:number, upmix:number|null}}
     */
    plan: function(format, sourceCh, selection){
        var maxCh = formatMax((format || '').toLowerCase());
        if(selection === 'auto') return { channels: Math.max(1, Math.min(sourceCh || 2, maxCh)), upmix: null };
        var sel = this.getValue();
        if(sel && sel > 2 && sel <= maxCh && canUpmixTo(sel, sourceCh)){
            return { channels: sourceCh, upmix: sel };
        }
        return { channels: this.outputChannels(format, sourceCh), upmix: null };
    }
};

// Drain queued calls captured by the stub registered in timbrica-core.js
// (defer-onerror race defense — Sentry 116943205, 2026-05-02). No-op when
// the stub helper isn't present (e.g. if loaded before timbrica-core.js).
if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('ChannelModule');

})();
